// A20 (08/10) — la STRUCTURE de l'observatoire pour les pages qui n'ont pas de commune
// (accueil, offres, FAQ, à propos, CGV) : combien de thématiques, lesquelles. La vérité vient
// du registre du serveur (`GET /api/observatoire/structure`, `tagging.structure_publique`) ;
// `constants.ts` n'est plus que le repli hors ligne.
//
// Les FAMILLES (`familles`, `themes[].famille`) restent lues ici pour les seules maquettes
// internes de l'administration (/admin/design) : depuis le 14/10/2026 (décision de Marc et
// Cyrille), aucune page publique ni de l'observatoire ne les affiche — on ne parle que de
// thématiques et de sections.
//
// Pourquoi une requête plutôt qu'une constante de build : l'arborescence à 20 thématiques
// est derrière un drapeau SERVEUR (`ARBORESCENCE_V2`). Le retour arrière est un changement
// de variable et un redémarrage du backend, sans reconstruire le front — une constante
// écrite ici dirait « 20 » à une prod revenue à 13, dans la vitrine comme dans la CGV.
//
// Les pages de l'observatoire (qui ont une commune) lisent la même chose dans `/meta`
// (`nb_themes`, `themes`) : elles n'ont pas besoin de ce module.
import { useEffect, useState } from 'react'
import axios from 'axios'
import { NB_THEMATIQUES, THEMATIQUES } from './constants'
import { API, type FamilleMeta } from './obs/types'

export interface ThemeStructure { id: string; label: string; color: string; famille: string | null }

export interface StructureObservatoire {
  arborescence: 1 | 2
  nb_themes: number
  familles: FamilleMeta[]
  themes: ThemeStructure[]
  // `false` = repli hors ligne (requête en cours, en échec, ou réponse illisible) : le nombre
  // est celui de `constants.ts`, la liste est vide et les pages énumèrent `THEMATIQUES`.
  servie: boolean
}

export const STRUCTURE_REPLI: StructureObservatoire = Object.freeze({
  arborescence: 1, nb_themes: NB_THEMATIQUES, familles: [], themes: [], servie: false,
}) as StructureObservatoire

/** Lit la réponse du serveur — `null` si elle ne ressemble pas au contrat. Défensif exprès :
    une page commerciale ne doit jamais afficher « undefined thématiques ». */
export function lireStructure(d: unknown): StructureObservatoire | null {
  if (!d || typeof d !== 'object') return null
  const o = d as Record<string, unknown>
  if (!Array.isArray(o.themes)) return null
  const themes: ThemeStructure[] = []
  for (const t of o.themes as Record<string, unknown>[]) {
    if (!t || typeof t.id !== 'string' || typeof t.label !== 'string' || !t.label) return null
    themes.push({ id: t.id, label: t.label, color: typeof t.color === 'string' ? t.color : '#0D1F3C',
                  famille: typeof t.famille === 'string' ? t.famille : null })
  }
  if (themes.length === 0) return null
  const familles = Array.isArray(o.familles)
    ? (o.familles as Record<string, unknown>[])
        .filter(f => f && typeof f.id === 'string' && typeof f.titre === 'string')
        .map(f => ({ id: f.id as string, titre: f.titre as string }))
    : []
  const nb = typeof o.nb_themes === 'number' && Number.isInteger(o.nb_themes) && o.nb_themes > 0
    ? o.nb_themes : themes.length
  return { arborescence: o.arborescence === 2 ? 2 : 1, nb_themes: nb, familles, themes, servie: true }
}

// Cache de MODULE : une requête par chargement de page, partagée par tous les composants
// (la vitrine compte les thématiques à cinq endroits). Un échec n'est PAS mis en cache —
// le composant suivant retentera ; une réponse lisible l'est pour toute la session.
let cache: StructureObservatoire | null = null
let enVol: Promise<StructureObservatoire | null> | null = null

export function chargerStructure(): Promise<StructureObservatoire | null> {
  if (cache) return Promise.resolve(cache)
  if (!enVol) {
    // `Promise.resolve().then(...)` : une exception SYNCHRONE d'axios (module simulé dans un
    // test, configuration absente) devient un rejet, donc un repli — jamais un écran blanc.
    enVol = Promise.resolve()
      .then(() => axios.get(`${API}/observatoire/structure`, { timeout: 10000 }))
      .then(r => {
        const s = lireStructure(r?.data)
        if (s) cache = s
        return s
      })
      .catch(() => null)
      .finally(() => { enVol = null })
  }
  return enVol
}

/** La structure servie ; `STRUCTURE_REPLI` pendant le chargement et en cas d'échec. */
export function useStructure(): StructureObservatoire {
  const [s, setS] = useState<StructureObservatoire>(cache ?? STRUCTURE_REPLI)
  useEffect(() => {
    let vivant = true
    chargerStructure().then(r => { if (vivant && r) setS(r) })
    return () => { vivant = false }
  }, [])
  return s
}

/** Tests seulement : oublie la structure en cache. */
export function viderCacheStructure(): void { cache = null; enVol = null }

const minusculeInitiale = (x: string) => x.charAt(0).toLocaleLowerCase('fr-FR') + x.slice(1)

/**
 * Les noms des thématiques tels qu'une phrase de vitrine les énumère.
 *
 * Arborescence v2 servie : les libellés du registre, initiale en minuscule (ils s'insèrent
 * dans une phrase). Sinon (v1 servie, ou repli) : `THEMATIQUES`, les noms courts de la vitrine
 * d'aujourd'hui, dans son ordre — drapeau éteint, le texte reste ainsi celui d'aujourd'hui au
 * caractère près (CDC A20, R3).
 */
export function nomsThematiques(s: StructureObservatoire): string[] {
  if (s.servie && s.arborescence === 2) return s.themes.map(t => minusculeInitiale(t.label))
  return [...THEMATIQUES]
}

/** Le séparateur d'une énumération de thématiques. Les libellés v2 contiennent eux-mêmes des
    virgules (« Culture, sport et loisirs ») : une virgule entre deux ferait lire vingt-trois
    thématiques au lieu de vingt. En v1 et en repli, la virgule d'aujourd'hui. */
export function separateurListe(s: StructureObservatoire): string {
  return s.servie && s.arborescence === 2 ? ' ; ' : ', '
}

const EN_LETTRES = ['zéro', 'une', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf',
  'dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit',
  'dix-neuf', 'vingt']

/** Un nombre de thématiques écrit en lettres (« treize », « vingt ») ; en chiffres au-delà. */
export function nombreEnLettres(n: number): string {
  return Number.isInteger(n) && n >= 0 && n < EN_LETTRES.length ? EN_LETTRES[n] : String(n)
}
